/**
 * Local outbox enqueue with coalescing (docs/08 §4).
 * Flush/push is Task 16.
 *
 * Callers must compute `payloadHash` *before* entering a Dexie transaction —
 * crypto.subtle (or any non-Dexie await) inside a live transaction causes
 * PrematureCommitError.
 */

import type { CardZappDB } from './database';
import type { OutboxEntry, SyncedTableName } from '../schemas/local-only';
import { APPEND_ONLY_TABLES, IMMUTABLE_TABLES } from '../repositories/entityRegistry';

export type OutboxOp = OutboxEntry['op'];

/**
 * Enqueue (or coalesce) one outbox entry inside an open Dexie transaction.
 * At most one pending entry per (table, rowId).
 */
export async function enqueueOutbox(
  db: CardZappDB,
  args: {
    table: SyncedTableName;
    op: OutboxOp;
    rowId: string;
    /** Full canonical row for insert/update; {} for delete. */
    payload: unknown;
    /** Precomputed SHA-256 hex of canonicalize(payload). */
    payloadHash: string;
    baseServerUpdatedAt: string | null;
    createdAt: string;
  },
): Promise<void> {
  if (
    (args.op === 'update' || args.op === 'delete') &&
    (APPEND_ONLY_TABLES.has(args.table) || IMMUTABLE_TABLES.has(args.table))
  ) {
    throw new Error(`outbox: ${args.table} is append-only/immutable; cannot enqueue ${args.op}`);
  }

  const pending = await findPending(db, args.table, args.rowId);

  if (pending === undefined) {
    const entry: OutboxEntry = {
      id: crypto.randomUUID(),
      table: args.table,
      op: args.op,
      rowId: args.rowId,
      payload: args.payload,
      payloadHash: args.payloadHash,
      baseServerUpdatedAt: args.baseServerUpdatedAt,
      createdAt: args.createdAt,
      attempts: 0,
    };
    await db.outbox.put(entry);
    return;
  }

  const next = coalesce(pending, args.op, args.payload, args.payloadHash);
  if (next === 'drop') {
    await db.outbox.delete(pending.id);
    return;
  }
  if (next === 'reject') {
    throw new Error('outbox: DELETE then UPDATE is not allowed; undelete must be a new INSERT');
  }
  await db.outbox.put(next);
}

async function findPending(
  db: CardZappDB,
  table: SyncedTableName,
  rowId: string,
): Promise<OutboxEntry | undefined> {
  const all = await db.outbox.toArray();
  return all.find((e) => e.table === table && e.rowId === rowId);
}

function coalesce(
  pending: OutboxEntry,
  newOp: OutboxOp,
  payload: unknown,
  hash: string,
): OutboxEntry | 'drop' | 'reject' {
  const { op } = pending;
  if (op === 'insert' && newOp === 'update') {
    return { ...pending, op: 'insert', payload, payloadHash: hash };
  }
  if (op === 'insert' && newOp === 'delete') {
    return 'drop';
  }
  if (op === 'update' && newOp === 'update') {
    return {
      ...pending,
      op: 'update',
      payload,
      payloadHash: hash,
      // original baseServerUpdatedAt retained
    };
  }
  if (op === 'update' && newOp === 'delete') {
    return {
      ...pending,
      op: 'delete',
      payload: {},
      payloadHash: hash,
    };
  }
  if (op === 'delete' && newOp === 'update') {
    return 'reject';
  }
  if (op === 'delete' && newOp === 'delete') {
    return pending; // idempotent no-op
  }
  if (op === 'insert' && newOp === 'insert') {
    return { ...pending, payload, payloadHash: hash };
  }
  return { ...pending, op: newOp, payload, payloadHash: hash };
}
